import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { OperatorGroupService } from '../../../../api/gen/api/operatorGroup.service';
import { CustomerService } from '../../../../api/gen/api/customer.service';
import { ParkingOperatorDto } from '../../../../api/gen/model/parkingOperatorDto';
import { CustomerDto } from '../../../../api/gen/model/customerDto';
import { OperatorGroupDto } from '../../../../api/gen/model/operatorGroupDto';
import { ParkingSpaceOperatorGroupDto } from '../../../../api/gen/model/parkingSpaceOperatorGroupDto';
import { SortOrder } from '../../../../api/gen/model/sortOrder';

export interface AssignParkingSpaceDialogData {
  operator: ParkingOperatorDto;
}

@Component({
  selector: 'app-assign-parking-space-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule
  ],
  templateUrl: './assign-parking-space-dialog.component.html',
  styleUrl: './assign-parking-space-dialog.component.scss'
})
export class AssignParkingSpaceDialogComponent implements OnInit {

  private dialogRef = inject(MatDialogRef<AssignParkingSpaceDialogComponent>);
  private data = inject<AssignParkingSpaceDialogData>(MAT_DIALOG_DATA);
  private operatorGroupService = inject(OperatorGroupService);
  private customerService = inject(CustomerService);
  private snackBar = inject(MatSnackBar);

  customers = signal<CustomerDto[]>([]);
  private rawGroups = signal<OperatorGroupDto[]>([]);
  selectedCustomerId = signal<string>('');
  selectedPsoId = signal<string>('');
  isLoadingCustomers = signal(false);
  isLoadingGroups = signal(false);
  isSubmitting = signal(false);

  get operator(): ParkingOperatorDto {
    return this.data.operator;
  }

  get operatorName(): string {
    return [this.operator.firstName, this.operator.lastName].filter(Boolean).join(' ') || this.operator.email || 'Unknown';
  }

  /** All parking-space-group junction entries for the selected customer. */
  get parkingSpaceGroups(): ParkingSpaceOperatorGroupDto[] {
    return this.rawGroups().flatMap(g => g.parkingSpaceOperators ?? []);
  }

  private get alreadyMemberGroupIds(): Set<string> {
    const ids = new Set<string>();
    for (const group of this.rawGroups()) {
      const isMember = (group.operatorGroupMemberships ?? []).some(m => m.operatorId === this.operator.id);
      if (isMember && group.id) ids.add(group.id);
    }
    return ids;
  }

  /** Parking-space groups the operator is not yet a member of. */
  get availableParkingSpaceGroups(): ParkingSpaceOperatorGroupDto[] {
    const memberIds = this.alreadyMemberGroupIds;
    return this.parkingSpaceGroups.filter(pso => pso.operatorGroupId && !memberIds.has(pso.operatorGroupId));
  }

  get selectedParkingSpaceGroup(): ParkingSpaceOperatorGroupDto | undefined {
    const id = this.selectedPsoId();
    return id ? this.parkingSpaceGroups.find(pso => pso.id === id) : undefined;
  }

  ngOnInit(): void {
    this.loadCustomers();
  }

  private loadCustomers(): void {
    this.isLoadingCustomers.set(true);
    this.customerService.customerFilterPost(undefined, false, 0, 100, 'name', SortOrder.Ascending).subscribe({
      next: (result) => {
        this.customers.set(result?.items ?? []);
        this.isLoadingCustomers.set(false);
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        this.snackBar.open('Failed to load customers', 'Close', { duration: 5000 });
        this.isLoadingCustomers.set(false);
      }
    });
  }

  onCustomerSelected(customerId: string): void {
    this.selectedCustomerId.set(customerId);
    this.selectedPsoId.set('');
    this.rawGroups.set([]);
    if (customerId) this.loadGroups(customerId);
  }

  private loadGroups(customerId: string): void {
    this.isLoadingGroups.set(true);
    this.operatorGroupService.operatorGroupsGet(
      customerId, undefined, true, true, false, 0, 100, 'name', SortOrder.Ascending
    ).subscribe({
      next: (result) => {
        this.rawGroups.set(result?.items ?? []);
        this.isLoadingGroups.set(false);
      },
      error: (error) => {
        console.error('Error loading groups:', error);
        this.snackBar.open('Failed to load groups', 'Close', { duration: 5000 });
        this.isLoadingGroups.set(false);
      }
    });
  }

  onSubmit(): void {
    const pso = this.selectedParkingSpaceGroup;
    const operatorGroupId = pso?.operatorGroupId;
    const operatorId = this.operator.id;
    if (!operatorGroupId || !operatorId) return;

    this.isSubmitting.set(true);
    this.operatorGroupService.operatorGroupsOperatorGroupIdOperatorsPost(operatorGroupId, { operatorId }).subscribe({
      next: () => {
        const spaceName = pso?.parkingSpace?.name ?? 'group';
        this.snackBar.open(`Operator added to ${spaceName} successfully`, 'Close', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error adding operator to group:', error);
        this.snackBar.open('Failed to add operator to group', 'Close', { duration: 5000 });
        this.isSubmitting.set(false);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}